import type { ClientEvent, InstallerFrom, SitePage } from '@lyrmo/shared';
import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import type { ApiClient } from '../../api/client';
import { useI18n } from '../../i18n/i18n-context';
import type { AppView } from '../../layout/useAppView';
import { useConsent } from '../../lib/consent-context';
import { arrivalOf, createTracker, localStore, noteUse } from '../../lib/tracking';
import { PageFromContext, TrackingContext } from '../../lib/tracking-context';

const PAGES: Readonly<Record<AppView, SitePage>> = {
  home: 'home',
  write: 'studio',
  pricing: 'pricing',
  desktop: 'desktop-apps',
  guide: 'how-it-works',
};

interface TrackingProviderProps {
  readonly client: Pick<ApiClient, 'recordEvents'>;
  /** The page on screen. */
  readonly view: AppView;
  readonly children: ReactNode;
}

/**
 * Notes visits, the pages seen, and what the writer does, for Lyrmo's own insights, and sends
 * them a few at a time: the last as the page is hidden or closed. Only once the visitor allows
 * it: before, and after they decline, nothing is noted, kept, or sent.
 */
export function TrackingProvider({ client, view, children }: TrackingProviderProps) {
  const { locale } = useI18n();
  const allowed = useConsent().choice === 'insights';
  // The page on screen, and the one before it, or none on arriving.
  const [pages, setPages] = useState<{ view: AppView; from: InstallerFrom }>({
    view,
    from: 'arrival',
  });
  if (pages.view !== view) setPages({ view, from: PAGES[pages.view] });
  const tracker = useMemo(
    () => createTracker((events, keepalive) => client.recordEvents(events, { keepalive })),
    [client],
  );
  // Where the writer is, for a visit that begins with something they do.
  const where = useRef({ view, locale });
  useEffect(() => {
    where.current = { view, locale };
  }, [view, locale]);

  const track = useCallback(
    (event: ClientEvent) => {
      if (!allowed) return;
      // Coming back to a page left open for a while is a visit too.
      if (noteUse(localStore(), Date.now())) {
        const { view: page, locale: language } = where.current;
        tracker.track({ event: 'visit', page: PAGES[page], language });
      }
      tracker.track(event);
    },
    [tracker, allowed],
  );

  // The page arrived at begins a visit, unless the site was in use moments ago; each page moved
  // to after it is seen. Allowing insights is arriving, on the page on screen.
  const shown = useRef<AppView | null>(null);
  useEffect(() => {
    if (!allowed) {
      shown.current = null;
      return;
    }
    if (shown.current === view) return;
    const arriving = shown.current === null;
    shown.current = view;
    if (!arriving) {
      track({ event: 'page', page: PAGES[view] });
    } else if (noteUse(localStore(), Date.now())) {
      tracker.track({
        event: 'visit',
        page: PAGES[view],
        language: locale,
        ...arrivalOf(window.location, document.referrer),
      });
    }
  }, [view, locale, track, tracker, allowed]);

  useEffect(() => {
    const onVisibilityChange = () => {
      if (document.visibilityState === 'hidden') tracker.flush(true);
    };
    const onPageHide = () => {
      tracker.flush(true);
    };
    document.addEventListener('visibilitychange', onVisibilityChange);
    window.addEventListener('pagehide', onPageHide);
    return () => {
      document.removeEventListener('visibilitychange', onVisibilityChange);
      window.removeEventListener('pagehide', onPageHide);
      tracker.flush();
    };
  }, [tracker]);

  return (
    <TrackingContext value={track}>
      <PageFromContext value={pages.from}>{children}</PageFromContext>
    </TrackingContext>
  );
}
